import { useUser } from "@/context/user-context";
import { observer } from "mobx-react-lite";

import { DataTable } from "@/components/ui/data-table";
import EmptyPlaceholder from "@/components/ui/EmptyPlaceholder";
import {
  ColumnDef,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { useReportData } from "../GraphView/hooks";
import { extractScoreFromReport } from "../GraphView/utils";
import { getProgressPercentage } from "./utils";
import { ReportTableData } from "./types";
import { TablePagination } from "@/components/ui/table-pagination";
import { ContextMenuItems } from "@/components/ui/ContextMenu";

const TableView = observer<{
  displayAll: boolean;
  usePromptId: boolean;
  useParentReportId: boolean;
  columns: ColumnDef<ReportTableData>[];
  contextMenuItems?: ContextMenuItems<ReportTableData>[];
  displayPlaceholder?: boolean;
}>(
  ({
    displayAll,
    usePromptId = true,
    useParentReportId = false,
    columns,
    contextMenuItems,
    displayPlaceholder = true,
  }) => {
    const {
      reports,
      isLoading,
      pageCount,
      setPage,
      setPageSize,
      page,
      pageSize,
      hasNext,
    } = useReportData({
      usePromptId,
      useParentReportId,
      displayAll,
    });

    const workspaceId = useUser()?.activeWorkspaceId;
    const [sorting, setSorting] = useState<SortingState>([
      { id: "createdAt", desc: true },
    ]);

    const tableData: ReportTableData[] = useMemo(
      () =>
        reports
          ? reports
              .filter((report) => report.parent_report_id)
              .map((report) => {
                let score;
                try {
                  score = extractScoreFromReport(report);
                } catch (error) {
                  console.error("Error extracting score:", error);
                  score = 0;
                }

                let createdAt;
                try {
                  createdAt = new Date(report.created_at);
                  if (isNaN(createdAt.getTime())) {
                    createdAt = new Date();
                  }
                } catch (error) {
                  createdAt = new Date();
                }

                return {
                  id: report.id || 0,
                  pipelineName: report.parent_report?.name || "Unknown",
                  batchName: report.name || "Unknown",
                  status: report.status || null,
                  datasetName: report?.dataset?.dataset_group?.name || null,
                  datasetGroupId: report?.dataset?.dataset_group?.id || null,
                  datasetId: report?.dataset?.id || null,
                  score: score,
                  promptVersionNumber: report.prompt_version_number || null,
                  createdAt: createdAt,
                  parentReportId: report.parent_report_id || 0,
                  progress: getProgressPercentage(report.status),
                  stillRunning: Boolean(
                    report.status?.RUNNING && report.status.RUNNING > 0,
                  ),
                };
              })
          : [],
      [reports],
    );

    const table = useReactTable({
      data: tableData,
      columns,
      state: {
        sorting,
      },
      onSortingChange: setSorting,
      getCoreRowModel: getCoreRowModel(),
      getSortedRowModel: getSortedRowModel(),
      pageCount: Math.ceil(tableData.length / 10),
    });

    if (!isLoading && (!reports || reports.length === 0)) {
      if (!displayPlaceholder) {
        return null;
      }

      return (
        <div className="flex h-full flex-col py-4">
          <EmptyPlaceholder
            icon
            className="h-full"
            message="No evaluation data available. Apply different filters or run evaluations on this prompt."
          />
        </div>
      );
    }

    return (
      <div className="flex h-full flex-col gap-2 py-4 pb-12">
        <DataTable<ReportTableData>
          table={table}
          isLoading={isLoading}
          emptyMessage={
            <EmptyPlaceholder
              icon
              className="h-full"
              message="No evaluation data available. Apply different filters or run evaluations on this prompt."
            />
          }
          linkTo={(row) =>
            `/workspace/${workspaceId}/evaluate/blueprints/${row.original.parentReportId}/reports/${row.original.id}`
          }
          contextMenuItems={contextMenuItems}
        />
        {isLoading ? null : (
          <TablePagination
            table={table}
            pages={pageCount as number}
            hasNext={hasNext ?? false}
            hasPrev
            page={page}
            pageSize={pageSize}
            pageSizeOptions={[10, 20, 25, 50, 100]}
            setPageIndex={setPage}
            setPageSize={setPageSize}
          />
        )}
      </div>
    );
  },
);

export default TableView;
